import { useMemo, useState } from "react";

import { useListAvailableInputs } from "@api/rest/types-and-hooks";

interface AvailableInput {
  key: string;
  source?: string | null;
  description?: string | null;
  /** Some parent inputs (``biographic_info``, ``interviews``) declare
   *  nested leaf names. The prompt yaml addresses individual leaves as
   *  ``<parent>.<leaf>`` (``biographic_info.name``,
   *  ``interviews.other``). Expand them here so the user can pick
   *  specific leaves instead of only the parent. */
  nested_keys?: string[] | null;
}

interface PromptInputsSelectProps {
  /** Selected input keys, order preserved. */
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}

const SOURCE_LABELS: Record<string, string> = {
  assessment: "Assessment",
  parsed_file: "Parsed File",
  report_section: "Report Section",
  biographic: "Biographic",
  interview: "Interview",
  clinician: "Clinician",
  goal_data: "Goal Data",
  computed: "Computed",
};

// Same stable ordering used by PromptDetail's read-only view.
const SOURCE_ORDER = [
  "assessment",
  "parsed_file",
  "report_section",
  "biographic",
  "interview",
  "clinician",
  "goal_data",
  "computed",
];

/**
 * Multi-select for prompt input keys, grouped by source. Fetches the full
 * catalog from ``GET /api/basecamp/prompts/available-inputs``.
 *
 * Selected order is preserved (first-toggled first). Un-toggling drops the
 * key. A search field filters within each source group.
 */
export const PromptInputsSelect: React.FC<PromptInputsSelectProps> = ({ value, onChange, disabled }) => {
  const { data, isLoading } = useListAvailableInputs();
  const rawInputs: AvailableInput[] = useMemo(
    () => (data?.status === 200 ? (data.data.inputs as AvailableInput[]) : []),
    [data],
  );
  // Expand parent inputs with ``nested_keys`` into per-leaf rows so
  // ``biographic_info.name`` / ``.age`` / ``.gender`` and
  // ``interviews.parent`` / ``.other`` are individually toggleable —
  // matching how the yaml addresses them in ``input_keys``. Without
  // this expansion the multi-select only offers parents, and a user
  // trying to pick a specific leaf has no checkbox for it.
  const inputs: AvailableInput[] = useMemo(() => {
    const out: AvailableInput[] = [];
    for (const inp of rawInputs) {
      out.push(inp);
      if (inp.nested_keys && inp.nested_keys.length > 0) {
        for (const leaf of inp.nested_keys) {
          out.push({
            key: `${inp.key}.${leaf}`,
            source: inp.source,
            description: `${inp.description ?? ""}${inp.description ? " — " : ""}${leaf}`,
          });
        }
      }
    }
    return out;
  }, [rawInputs]);

  // Note to callers: to clear the search filter after an external event
  // (e.g., saving a draft), pass a changing `key` prop from the parent.
  // React remounts the component; the query state resets to "". Uses the
  // React Query cache under the hood, so no re-fetch happens.
  const [query, setQuery] = useState("");

  const selected = useMemo(() => new Set(value), [value]);
  const knownKeys = useMemo(() => new Set(inputs.map((i) => i.key)), [inputs]);

  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [pasteWarning, setPasteWarning] = useState<string | null>(null);

  const applyPaste = () => {
    // Accept anything reasonable a user would paste: newlines, commas,
    // semicolons, whitespace. Strip surrounding quotes/backticks so
    // ``"assessments.abas"`` and ``\`assessments.abas\`` both work.
    const parts = pasteText
      .split(/[\s,;]+/)
      .map((s) => s.trim().replace(/^["'`]+|["'`]+$/g, ""))
      .filter(Boolean);
    const unique = Array.from(new Set(parts));
    const valid = unique.filter((k) => knownKeys.has(k));
    const invalid = unique.filter((k) => !knownKeys.has(k));
    const toAdd = valid.filter((k) => !selected.has(k));
    if (toAdd.length) onChange([...value, ...toAdd]);
    setPasteWarning(
      invalid.length > 0
        ? `Ignored ${invalid.length} unrecognized key${invalid.length === 1 ? "" : "s"}: ${invalid.slice(0, 5).join(", ")}${invalid.length > 5 ? "…" : ""}`
        : toAdd.length === 0 && valid.length > 0
          ? "All pasted keys were already selected."
          : null,
    );
    setPasteText("");
    // Close only when we actually added new keys with no rejects to
    // report. Any warning (invalid keys OR "already selected") renders
    // inside the panel, so leaving it open is what makes the feedback
    // visible.
    if (invalid.length === 0 && toAdd.length > 0) setPasteOpen(false);
  };

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q ? inputs.filter((i) => i.key.toLowerCase().includes(q)) : inputs;
    return filtered.reduce<Record<string, AvailableInput[]>>((acc, i) => {
      const src = i.source ?? "other";
      (acc[src] ??= []).push(i);
      return acc;
    }, {});
  }, [inputs, query]);

  const orderedSources = useMemo(() => {
    const known = SOURCE_ORDER.filter((s) => grouped[s]);
    const extras = Object.keys(grouped)
      .filter((s) => !SOURCE_ORDER.includes(s))
      .sort();
    return [...known, ...extras];
  }, [grouped]);

  const toggle = (key: string) => {
    if (selected.has(key)) {
      onChange(value.filter((k) => k !== key));
    } else {
      // Append preserves first-toggled order — matches how yaml files list
      // required_inputs after inputs, which the backend flattens in the
      // same order.
      onChange([...value, key]);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {value.length > 0 && (
        <div className="flex flex-wrap items-start gap-1 rounded-md border border-border-subtle bg-bg-default p-2">
          {value.map((k) => {
            const unknown = !isLoading && !knownKeys.has(k);
            return (
              <span
                key={k}
                title={unknown ? `Unknown key — not in the available-inputs catalog` : k}
                className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-mono ${
                  unknown ? "border-red-300 bg-red-50 text-red-700" : "border-rio-200 bg-rio-50 text-rio-800"
                }`}
              >
                {k}
                <button
                  type="button"
                  onClick={() => toggle(k)}
                  disabled={disabled}
                  aria-label={`Remove ${k}`}
                  className="cursor-pointer text-rio-600 hover:text-rio-800 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}
      {pasteOpen ? (
        // Paste mode: self-contained panel taking the same visual slot
        // the search input normally occupies. Cancel + Add sit inside
        // the panel next to the helper text so nothing floats outside
        // the field's container.
        <div className="flex flex-col gap-2 rounded-md border border-border-subtle bg-bg-default p-3">
          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={"assessments.abas\nassessments.ados_2, biographic_info.age\ninterviews.parent"}
            disabled={disabled}
            rows={4}
            autoFocus
            className="w-full rounded-md border border-border-medium bg-bg-surface px-2 py-1.5 text-xs font-mono text-text-primary focus:outline-none focus:ring-1 focus:ring-rio-400"
          />
          <div className="flex items-center gap-2">
            <span className="flex-1 text-xs text-text-tertiary">
              Newlines, commas, or spaces. Unknown keys are ignored.
            </span>
            <button
              type="button"
              onClick={() => {
                setPasteOpen(false);
                setPasteText("");
                setPasteWarning(null);
              }}
              className="shrink-0 rounded-md border border-border-medium bg-bg-surface px-3 py-1.5 text-xs text-text-primary cursor-pointer hover:bg-limestone-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={applyPaste}
              disabled={disabled || !pasteText.trim()}
              className="shrink-0 rounded-md bg-rio-600 px-3 py-1.5 text-xs font-medium text-white cursor-pointer hover:bg-rio-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Add keys
            </button>
          </div>
          {pasteWarning && <div className="text-xs text-red-600">{pasteWarning}</div>}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={query}
            placeholder="Search input keys..."
            onChange={(e) => setQuery(e.target.value)}
            disabled={disabled || isLoading}
            className="flex-1 rounded-md border border-border-medium bg-bg-default px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-rio-400"
          />
          <button
            type="button"
            onClick={() => {
              setPasteOpen(true);
              setPasteWarning(null);
            }}
            disabled={disabled || isLoading}
            className="shrink-0 rounded-md border border-border-medium bg-bg-default px-3 py-2 text-sm text-text-primary cursor-pointer hover:bg-limestone-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Paste list
          </button>
        </div>
      )}
      <div className="flex flex-col gap-3 max-h-80 overflow-y-auto rounded-md border border-border-subtle bg-bg-default p-3">
        {isLoading ? (
          <div className="flex flex-col gap-2">
            {/* Skeleton rows — one "source header" bar + a few "row" bars.
                Matches the visual density of a loaded list so the panel
                doesn't jump when data lands. */}
            {[0, 1, 2].map((groupIdx) => (
              <div key={groupIdx} className="flex flex-col gap-1">
                <div className="h-3 w-32 rounded bg-limestone-200 animate-pulse" />
                {[0, 1, 2].map((rowIdx) => (
                  <div key={rowIdx} className="h-5 w-full rounded bg-limestone-100 animate-pulse" />
                ))}
              </div>
            ))}
          </div>
        ) : orderedSources.length === 0 ? (
          <div className="text-xs text-text-tertiary italic">No matching inputs.</div>
        ) : null}
        {orderedSources.map((source) => (
          <div key={source} className="flex flex-col gap-1">
            <div className="text-xs font-medium text-text-secondary uppercase tracking-wide">
              {SOURCE_LABELS[source] ?? source}
            </div>
            {grouped[source].map((input) => (
              <label
                key={input.key}
                className="flex items-start gap-2 text-sm text-text-primary cursor-pointer hover:bg-limestone-50 rounded px-1 py-0.5"
              >
                <input
                  type="checkbox"
                  checked={selected.has(input.key)}
                  disabled={disabled}
                  onChange={() => toggle(input.key)}
                  className="mt-1"
                />
                <div className="flex flex-col">
                  <code className="text-xs font-mono">{input.key}</code>
                  {input.description && <span className="text-xs text-text-tertiary">{input.description}</span>}
                </div>
              </label>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
